import {
    Tooltip as ShadcnTooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger as ShadcnTooltipTrigger,
} from "@/components/ui/tooltip";
import { cx } from "@/utils/cx";
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

type TooltipPlacement =
    | "top"
    | "top left"
    | "top right"
    | "top start"
    | "top end"
    | "bottom"
    | "bottom left"
    | "bottom right"
    | "bottom start"
    | "bottom end"
    | "left"
    | "left top"
    | "left bottom"
    | "start"
    | "start top"
    | "start bottom"
    | "right"
    | "right top"
    | "right bottom"
    | "end"
    | "end top"
    | "end bottom";

type TooltipProps = {
    title: ReactNode;
    description?: ReactNode;
    arrow?: boolean;
    delay?: number;
    children: ReactNode;
    closeDelay?: number;
    isDisabled?: boolean;
    isOpen?: boolean;
    defaultOpen?: boolean;
    offset?: number;
    crossOffset?: number;
    placement?: TooltipPlacement;
    onOpenChange?: (isOpen: boolean) => void;
    className?: string;
    id?: string;
    style?: CSSProperties;
};

export function Tooltip({
    title,
    description,
    children,
    arrow = false,
    delay = 300,
    closeDelay = 0,
    isDisabled,
    isOpen,
    defaultOpen,
    offset = 6,
    crossOffset,
    placement = "top",
    onOpenChange,
    className,
    id,
    style,
}: TooltipProps) {
    const isTopOrBottomLeft = ["top left", "top end", "bottom left", "bottom end"].includes(placement);
    const isTopOrBottomRight = ["top right", "top start", "bottom right", "bottom start"].includes(placement);
    const calculatedCrossOffset = isTopOrBottomLeft ? -12 : isTopOrBottomRight ? 12 : 0;
    const [placementSide, placementAlign] = placement.split(" ");
    const side =
        placementSide === "start"
            ? "inline-start"
            : placementSide === "end"
              ? "inline-end"
              : placementSide === "bottom" || placementSide === "left" || placementSide === "right"
                ? placementSide
                : "top";
    const align =
        placementAlign === "left" || placementAlign === "top" || placementAlign === "start"
            ? "start"
            : placementAlign === "right" || placementAlign === "bottom" || placementAlign === "end"
              ? "end"
              : "center";

    return (
        <TooltipProvider delay={delay} closeDelay={closeDelay}>
            <ShadcnTooltip
                disabled={isDisabled}
                open={isOpen}
                defaultOpen={defaultOpen}
                onOpenChange={(open) => onOpenChange?.(open)}
            >
                {children}
                <TooltipContent
                    id={id}
                    role="tooltip"
                    style={style}
                    side={side}
                    sideOffset={offset}
                    align={align}
                    alignOffset={crossOffset ?? calculatedCrossOffset}
                    className={cx(
                        "flex max-w-xs flex-col items-start gap-1 px-3 shadow-lg",
                        description ? "py-3" : "py-2",
                        !arrow && "[&>[data-side]:last-child]:hidden",
                        className,
                    )}
                >
                    <span className="text-xs font-semibold">{title}</span>
                    {description ? <span className="text-xs font-medium text-background/80">{description}</span> : null}
                </TooltipContent>
            </ShadcnTooltip>
        </TooltipProvider>
    );
}

type TooltipTriggerProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    isDisabled?: boolean;
};

export function TooltipTrigger({
    children,
    className,
    disabled,
    isDisabled,
    type = "button",
    ...buttonProps
}: TooltipTriggerProps) {
    const triggerDisabled = isDisabled ?? disabled;

    return (
        <ShadcnTooltipTrigger
            disabled={triggerDisabled}
            render={
                <button
                    {...buttonProps}
                    type={type}
                    disabled={triggerDisabled}
                    className={cx("h-max w-max outline-hidden", className)}
                />
            }
        >
            {children}
        </ShadcnTooltipTrigger>
    );
}
